import AutoLoadingButton from "@/components/AutoLoadingButton";
import WaybillTemplateSelect2 from "@/components/WaybillTemplateSelect2";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { useModalStore } from "@/hooks/Modal/hooks";
import { getRunningAutoPrintTask } from "@/services/api/autoPrint";
import { getAutoPushOrderToSupplierSetting } from "@/services/api/dropShipping/autoPushOrderSupplierSetting";
import { InstantShipPostPayInterval } from "@/services/api/instantShip";
import type { OpenInstantShipTaskParams } from "@/services/api/instantShip";
import { useGlobalStore } from "@/store/hooks";
import { Alert, Button, Form, Modal, Select } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import EwaybillQuantityInfo from "../dialogs/PrintWaybillDialog/EwaybillQuantityInfo";
import ShippingAddressSelect from "../select/ShippingAddressSelect";
import InstantShipStore from "../../store/InstantShipStore";
import { instantShipPostPayIntervalOptions } from "./constants";
import DisclaimerModal from "./DisclaimerModal";
import { buildOpenInstantShipTaskParams } from "./services";
import TaskModal from "./TaskModal";

interface StartModalProps extends InjectBaseModalProps {
    shop: ShopDTO;
    instantShipStore: InstantShipStore;
}

interface StartFormValues {
    postPayInterval: InstantShipPostPayInterval;
    waybillTemplate: WaybillTemplateDTO;
    shippingAddress: ShippingAddressDTO;
}

const ewaybillTemplateTypes = Object.values(WaybillTemplateType).filter((type) => type !== WaybillTemplateType.NORMAL);

interface WrapTemplateSelectExtraProps {
    template: WaybillTemplateDTO;
}

const WrapTemplateSelectExtra: React.FC<WrapTemplateSelectExtraProps> = observer(function (props) {
    const { template } = props;
    const globalStore = useGlobalStore();

    if (!template) return null;

    const account = globalStore.ewaybillAccountStore.ewaybillAccountMapping.get(template.ewaybillShippingAddressId);

    return (
        <div>
            <span>电子面单账号：{account?.name || account?.platformUsername || "-"}</span>
            <EwaybillQuantityInfo template={template} />
        </div>
    );
});

const StartModal: React.FC<StartModalProps> = observer(function (props) {
    const { open, onCancel, shop, instantShipStore } = props;
    const [form] = Form.useForm<StartFormValues>();
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();
    const configLoading =
        globalStore.waybillPrintStore.waybillTemplatesLoading ||
        globalStore.waybillPrintStore.shippingAddressesLoading ||
        globalStore.ewaybillAccountStore.loading;

    useEffect(() => {
        Promise.all([globalStore.waybillPrintStore.ensure(), globalStore.ewaybillAccountStore.ensure()]).catch(
            () => undefined,
        );
    }, []);

    async function handleStart() {
        const values = await form.validateFields();

        const currentTask = await instantShipStore.loadCurrentTask(shop.id, false);
        if (currentTask) {
            Modal.error({ title: "当前店铺已开启极速发货，请先关闭现有任务" });
            return;
        }

        const autoPrintTasks = await getRunningAutoPrintTask([shop.id]);
        if (autoPrintTasks?.length) {
            Modal.error({
                title: "当前店铺正在自动打印，不支持开启极速发货",
                content: "请先关闭该店铺的自动打印任务后再开启极速发货。",
            });
            return;
        }

        const autoPushSetting = await getAutoPushOrderToSupplierSetting(shop.id);
        if (autoPushSetting?.enabled) {
            Modal.error({
                title: "当前店铺已开启分销自动推单，不支持开启极速发货",
                content: "请先关闭分销自动推单功能后再开启极速发货。",
            });
            return;
        }

        let openParams: OpenInstantShipTaskParams;
        try {
            openParams = buildOpenInstantShipTaskParams(
                globalStore,
                shop,
                values.postPayInterval,
                values.waybillTemplate,
                values.shippingAddress,
            );
        } catch (error) {
            Modal.error({ title: error instanceof Error ? error.message : "极速发货配置不完整，请检查后重试" });
            return;
        }

        onCancel();

        modalStore.openModal(DisclaimerModal, {
            async onConfirm() {
                const task = await instantShipStore.openTask(openParams);

                onCancel();
                modalStore.openModal(TaskModal, {
                    shop,
                    instantShipStore,
                    taskId: task.id,
                });
            },
        });
    }

    return (
        <Modal
            width={1000}
            className="instant-ship-start-modal"
            title="开启极速发货"
            open={open}
            onCancel={onCancel}
            keyboard={false}
            mask={{ closable: false }}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    关闭
                </Button>,
                <AutoLoadingButton key="start" type="primary" loading={configLoading} onClick={handleStart}>
                    开启极速发货
                </AutoLoadingButton>,
            ]}
        >
            <Alert
                className="instant-ship-start-tip"
                type="warning"
                showIcon
                title={
                    <div>
                        【有助降低发货前退款率】开启后会根据极速发货规则自动取号并发货，请及时关注极速发货订单并打印快递单发包裹。
                        <a
                            target="_blank"
                            href="https://qw15p3s8hv.feishu.cn/wiki/NNQDwhTGAi5virkI6rscOFrQnTf"
                            className="ml5"
                        >
                            查看极速发货教程
                        </a>
                    </div>
                }
            />

            <Form<StartFormValues>
                form={form}
                labelCol={{ span: 3 }}
                wrapperCol={{ span: 19 }}
                initialValues={{ postPayInterval: InstantShipPostPayInterval.THIRTY_MINUTES }}
            >
                <Form.Item label="极速发货规则" required>
                    <div>
                        <Form.Item
                            name="postPayInterval"
                            rules={[{ required: true, message: "请选择极速发货规则" }]}
                            noStyle
                        >
                            <Select options={instantShipPostPayIntervalOptions} style={{ width: 150 }} />
                        </Form.Item>

                        <span className="ml5">的订单自动取号并发货</span>
                    </div>
                </Form.Item>

                <Form.Item<StartFormValues>
                    noStyle
                    shouldUpdate={(prev, current) => prev.waybillTemplate !== current.waybillTemplate}
                >
                    {({ getFieldValue }) => {
                        const template: WaybillTemplateDTO = getFieldValue("waybillTemplate");

                        return (
                            <Form.Item
                                name="waybillTemplate"
                                label="快递单模板"
                                extra={<WrapTemplateSelectExtra template={template} />}
                                rules={[{ required: true, message: "请选择快递单模板" }]}
                            >
                                <WaybillTemplateSelect2
                                    shopId={shop.id}
                                    waybillTemplateTypes={ewaybillTemplateTypes}
                                    popupMatchSelectWidth={false}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>

                <Form.Item<StartFormValues>
                    noStyle
                    shouldUpdate={(prev, current) => prev.waybillTemplate !== current.waybillTemplate}
                >
                    {({ getFieldValue }) => {
                        const waybillTemplate: WaybillTemplateDTO = getFieldValue("waybillTemplate");

                        return (
                            <Form.Item
                                name="shippingAddress"
                                label="发货地址"
                                valuePropName="checked"
                                rules={[{ required: true, message: "请选择发货地址" }]}
                            >
                                <ShippingAddressSelect
                                    waybillTemplate={waybillTemplate}
                                    shopId={shop.id}
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </Form>
        </Modal>
    );
});

export default StartModal;
